import React, { useCallback } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Color } from '@diritto/common/style/color';
import { GameWeeklyNumber } from '@diritto/common/promotion/2025-balance-ritto.constants';
import { PromotionReplyOrder, PromotionReplyOrderText } from '@diritto/common/promotion/constants';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { numberComma } from '@diritto/common/utils/string';

interface PromotionReplyTabProps {
  className?: string;
  orderBy: PromotionReplyOrder;
  orderList: PromotionReplyOrder[];
  week: GameWeeklyNumber;
  weeklyCount: number;
  weeklyList: GameWeeklyNumber[];
  onChangeOrderBy?: (key: PromotionReplyOrder) => void;
  onChangeWeek?: (key: GameWeeklyNumber) => void;
}

export const PromotionReplyTab = ({
  className,
  orderBy,
  orderList = [PromotionReplyOrder.Latest, PromotionReplyOrder.Like],
  week,
  weeklyCount,
  weeklyList,
  onChangeOrderBy,
  onChangeWeek,
}: PromotionReplyTabProps) => {
  const handleChangeOrderBy = useCallback(
    (key: PromotionReplyOrder) => {
      onChangeOrderBy?.(key);
    },
    [onChangeOrderBy],
  );

  const handleChangeWeekly = useCallback(
    (key: GameWeeklyNumber) => {
      onChangeWeek?.(key);
    },
    [onChangeWeek],
  );

  return (
    <StyledTabMenu className={className}>
      <div className={`flex flex-row items-center justify-center gap-3 border-b-2 h-full`}>
        {weeklyList.map(weekly => (
          <StyledTabButton
            key={weekly}
            type="button"
            isSelected={week === weekly}
            onClick={() => {
              if (week !== weekly) {
                handleChangeWeekly(weekly);
              }
            }}>
            {weekly}주차 {week === weekly && weeklyCount > 0 ? `(${numberComma(weeklyCount)})` : ''}
          </StyledTabButton>
        ))}
      </div>
      <div className="flex gap-[10px] h-full">
        {orderList.map(order => (
          <StyledOrderButton
            key={order}
            type="button"
            isSelected={orderBy === order}
            onClick={() => handleChangeOrderBy(order)}>
            {PromotionReplyOrderText[order]}
          </StyledOrderButton>
        ))}
      </div>
    </StyledTabMenu>
  );
};

const StyledTabMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  border-bottom: 1px solid ${Color.Gray200};

  & > p {
    font-weight: ${FontWeight.Bold};
    & > span {
      font-weight: ${FontWeight.Regular};
    }
  }
`;

const StyledTabButton = styled.button(
  ({ isSelected }: { isSelected: boolean }) => css`
    height: 56px;
    font-weight: ${isSelected ? FontWeight.Bold : FontWeight.Regular};
    ${Typography.XXSmall};

    border-bottom: 2px solid ${isSelected ? Color.Black : 'transparent'};
    margin-bottom: -1px;
  `,
);

const StyledOrderButton = styled.button(
  ({ isSelected }: { isSelected: boolean }) => css`
    height: 56px;
    font-weight: ${isSelected ? FontWeight.Bold : FontWeight.Regular};
    ${Typography.XXSmall};
  `,
);
